import { useEffect, useState } from 'react';

import { AppConfig } from '@config';
import { usePreviousValue } from '@endurancewarranty/ui';
import { AgentStatusEnum, SoftphoneIntegrationEnum } from '@lib/enums';
import { impersonationAtom } from '@states/impersonation/impersonationAtom';
import { useJotaiValue } from '@states/jotai/hooks';
import { currentCallAtom } from '@states/softphone/currentCallAtom';
import { softphoneAtom } from '@states/softphone/softphoneAtom';
import { useApplySoftphoneSession } from '@states/softphone/useApplySoftphoneSession';
import { useSoftphoneWorker } from '@states/softphone/useSoftphoneWorker';

import type { FC, ReactNode } from 'react';

const ACTIVE_CALL_STATUS = [
  AgentStatusEnum.Acw,
  AgentStatusEnum.InboundContact,
  AgentStatusEnum.OutboundContact,
];

type Props = Readonly<{
  children: ReactNode;
}>;

export const ContactSession: FC<Props> = ({ children }) => {
  const { lastTouchedQuoteId } = useJotaiValue(currentCallAtom);
  const { agentStatus } = useJotaiValue(softphoneAtom);
  const { previousValue: previousAgentStatus } = usePreviousValue(agentStatus);
  const { azureAdId } = useJotaiValue(impersonationAtom);
  const [dispositionQuoteId, setDispositionQuoteId] = useState<Maybe<string>>(null);

  const isSoftphoneEnabled =
    azureAdId == null &&
    AppConfig.softphoneIntegration.client === SoftphoneIntegrationEnum.Contacts;

  const { session } = useSoftphoneWorker({
    agentStatus,
    isEnabled: isSoftphoneEnabled,
  });

  useApplySoftphoneSession(session);

  useEffect(() => {
    const isPreviousStatusOnCall =
      previousAgentStatus != null && ACTIVE_CALL_STATUS.includes(previousAgentStatus);
    const isCurrentStatusOffCall = !ACTIVE_CALL_STATUS.includes(agentStatus);
    const isCallEnded = isPreviousStatusOnCall && isCurrentStatusOffCall;

    if (isCallEnded && dispositionQuoteId == null) {
      setDispositionQuoteId(lastTouchedQuoteId);
    }
  }, [dispositionQuoteId, previousAgentStatus, lastTouchedQuoteId, agentStatus]);

  return <>{children}</>;
};
